import React, { useEffect, useState } from 'react';
import {
  Box, Stack, TextField, Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
  Chip, Switch, Tooltip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import LockResetIcon from '@mui/icons-material/LockReset';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { useAuth } from 'contexts/AuthContext';
import { LoadingOverlay, EmptyState, ConfirmDialog, GlassIconButton } from 'dashboard/components/common';
import AdminTableFooter from './AdminTableFooter';
import SortableHeader from './SortableHeader';
import UserDetailsDrawer from './UserDetailsDrawer';

const PAGE_SIZE = 50;

export default function UsersTab() {
  const { user: me } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ sort_by: null, sort_dir: null });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [detailsId, setDetailsId] = useState(null);

  const onExport = () => {
    setExporting(true);
    API.adminExportUsers({
      search,
      status: statusFilter === 'all' ? undefined : statusFilter,
      sort_by: sort.sort_by || undefined,
      sort_dir: sort.sort_dir || undefined,
    })
      .catch((err) => toast.error(err?.message || 'Failed to export users'))
      .finally(() => setExporting(false));
  };

  const sendReset = (user) => {
    setBusyId(user._id);
    API.adminSendReset({ _id: user._id })
      .then((data) => toast.success(`Reset link sent to ${data?.email || user.email}`))
      .catch((err) => toast.error(err?.message || 'Failed to send reset email'))
      .finally(() => setBusyId(null));
  };

  // New search resets to page 1 so the user doesn't end up on an
  // empty page beyond the new result set.
  useEffect(() => { setPage(1); }, [search, statusFilter, sort.sort_by, sort.sort_dir]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      API.adminListUsers({
        search,
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
        sort_by: sort.sort_by || undefined,
        sort_dir: sort.sort_dir || undefined,
      })
        .then((res) => {
          if (cancelled) return;
          setRows(res?.items || []);
          setTotal(res?.total || 0);
        })
        .catch((err) => toast.error(err?.message || 'Failed to load users', { toastId: 'admin-users' }))
        .finally(() => !cancelled && setLoading(false));
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, statusFilter, page, sort.sort_by, sort.sort_dir]);

  const applyToggle = (user, nextActive) => {
    setBusyId(user._id);
    API.adminSetUserActive({ _id: user._id, is_active: nextActive })
      .then(() => {
        setRows((cur) => cur.map((u) => (u._id === user._id ? { ...u, is_active: nextActive } : u)));
        toast.success(nextActive ? 'User reactivated' : 'User disabled');
      })
      .catch((err) => toast.error(err?.message || 'Failed to update user'))
      .finally(() => { setBusyId(null); setConfirm(null); });
  };

  return (
    <Box>
      <Stack direction='row' alignItems='center' mb='12px' spacing='12px' flexWrap='wrap'>
        <TextField
          size='small'
          placeholder='Search by email, username, name…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 320 }}
        />
        <ToggleButtonGroup
          size='small'
          exclusive
          value={statusFilter}
          onChange={(_e, v) => v && setStatusFilter(v)}
        >
          <ToggleButton value='all'>All</ToggleButton>
          <ToggleButton value='active'>Active</ToggleButton>
          <ToggleButton value='disabled'>Disabled</ToggleButton>
        </ToggleButtonGroup>
        <Box flex={1} />
        <GlassIconButton
          size='small'
          label='Download CSV of the current view'
          disabled={exporting}
          disabledReason={exporting ? 'Export already running — the file will download when it is ready.' : ''}
          onClick={onExport}
        >
          <DownloadIcon fontSize='small' />
        </GlassIconButton>
      </Stack>

      <LoadingOverlay loading={loading} minHeight={240}>
        {rows.length === 0 && !loading ? (
          <EmptyState>No users match your search.</EmptyState>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <SortableHeader field='email'     sortBy={sort.sort_by} sortDir={sort.sort_dir} onSort={setSort}>Email</SortableHeader>
                <SortableHeader field='username'  sortBy={sort.sort_by} sortDir={sort.sort_dir} onSort={setSort}>Username</SortableHeader>
                <TableCell>Roles</TableCell>
                <TableCell>Stripe</TableCell>
                <TableCell>Active</TableCell>
                <SortableHeader field='createdAt' sortBy={sort.sort_by} sortDir={sort.sort_dir} onSort={setSort}>Joined</SortableHeader>
                <TableCell align='right'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((u) => (
                <TableRow
                  key={u._id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    ...(u.is_active === false ? { '& td': { color: 'text.disabled' } } : {}),
                  }}
                  onClick={() => setDetailsId(u._id)}
                >
                  <TableCell sx={{ '&:hover': { color: 'primary.main' } }}>{u.email}</TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>
                    {(u.roles || []).map((r) => (
                      <Chip key={r} size='small' label={r} sx={{ mr: '4px' }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip size='small' color={u.has_stripe ? 'success' : 'default'} label={u.has_stripe ? 'yes' : 'no'} />
                  </TableCell>
                  <TableCell>
                    {String(u._id) === String(me?._id) ? (
                      <Tooltip title="You can't disable your own account">
                        <Chip size='small' label='you' color='primary' variant='outlined' />
                      </Tooltip>
                    ) : (
                      <Tooltip title={u.is_active ? 'Disable account' : 'Reactivate account'}>
                        <span onClick={(e) => e.stopPropagation()}>
                          <Switch
                            size='small'
                            checked={u.is_active}
                            disabled={busyId === u._id}
                            onChange={(e) => {
                              const next = e.target.checked;
                              if (next) applyToggle(u, true);
                              else setConfirm({ user: u, next });
                            }}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</TableCell>
                  <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                    <GlassIconButton
                      size='small'
                      label='Send password reset email'
                      disabled={busyId === u._id || !u.email}
                      // "No email on file" is the invisible one — without it
                      // the button is permanently greyed for those users with
                      // nothing on screen saying why.
                      disabledReason={
                        busyId === u._id
                          ? 'This account is already being updated — give it a moment.'
                          : !u.email
                            ? 'This account has no email address on file, so there is nowhere to send the reset link.'
                            : ''
                      }
                      onClick={() => sendReset(u)}
                    >
                      <LockResetIcon fontSize='small' />
                    </GlassIconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            </Table>
          </TableContainer>
        )}
      </LoadingOverlay>
      <AdminTableFooter
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onChange={setPage}
        label='users'
      />
      <ConfirmDialog
        open={!!confirm}
        title='Disable account?'
        message={confirm ? `${confirm.user.email} won't be able to sign in until you re-enable them. Existing sessions stay until they expire.` : ''}
        confirmLabel='Disable'
        confirmColor='error'
        busy={!!busyId}
        onConfirm={() => confirm && applyToggle(confirm.user, confirm.next)}
        onCancel={() => setConfirm(null)}
      />
      <UserDetailsDrawer
        userId={detailsId}
        onClose={() => setDetailsId(null)}
      />
    </Box>
  );
}
